import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { Layout } from '@/components/Layout'
import { FeatureGuard } from '@/components/FeatureGuard'
import { LoginPage } from '@/pages/LoginPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { Dashboard } from '@/features/dashboard'

// Lazy load heavy features (barcode scanner = ~500KB)
const PckPocketV3 = lazy(() => import('@/features/pck-pocket/views/PckPocketV3').then(m => ({ default: m.PckPocketV3 })))
const OrderDetailView = lazy(() => import('@/features/pck-pocket/views/OrderDetailView').then(m => ({ default: m.OrderDetailView })))

// Lazy load order-import
const ImportView = lazy(() => import('@/features/order-import').then(m => ({ default: m.ImportView })))

// Lazy load delivery views
const PendingView = lazy(() => import('@/features/order-pending').then(m => ({ default: m.PendingView })))
const DeliveredView = lazy(() => import('@/features/delivery/views/DeliveredView').then(m => ({ default: m.DeliveredView })))

// Lazy load parking views
const ParkView = lazy(() => import('@/features/parking/views/ParkView').then(m => ({ default: m.ParkView })))
const ParkedView = lazy(() => import('@/features/parking/views/ParkedView').then(m => ({ default: m.ParkedView })))
const SplitView = lazy(() => import('@/features/split-order/views/SplitView').then(m => ({ default: m.SplitView })))
const PartialOrderView = lazy(() => import('@/features/split-order/views/PartialOrderView').then(m => ({ default: m.PartialOrderView })))

// Lazy load ordrelogg
const OrdreloggView = lazy(() => import('@/features/ordrelogg/views/OrdreloggView').then(m => ({ default: m.OrdreloggView })))
const CustomerChatView = lazy(() => import('@/features/ordrelogg/views/CustomerChatView').then(m => ({ default: m.CustomerChatView })))

// Lazy load verify (customer magic-link)
const VerifyDeliveryView = lazy(() => import('@/features/verify').then(m => ({ default: m.VerifyDeliveryView })))

// Lazy load verksted
const VerkstedDashboard = lazy(() => import('@/features/verksted/views/VerkstedDashboard').then(m => ({ default: m.VerkstedDashboard })))
const VerkstedInbox = lazy(() => import('@/features/verksted/views/VerkstedInbox').then(m => ({ default: m.VerkstedInbox })))
const VerkstedInProgress = lazy(() => import('@/features/verksted/views/VerkstedInProgress').then(m => ({ default: m.VerkstedInProgress })))
const VerkstedCompleted = lazy(() => import('@/features/verksted/views/VerkstedCompleted').then(m => ({ default: m.VerkstedCompleted })))
const VerkstedHistory = lazy(() => import('@/features/verksted/views/VerkstedHistory').then(m => ({ default: m.VerkstedHistory })))
const VerkstedCaseView = lazy(() => import('@/features/verksted/views/VerkstedCaseView').then(m => ({ default: m.VerkstedCaseView })))

// Lazy load admin
const AdminDashboard = lazy(() => import('@/features/admin/views/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const AdminRoutingView = lazy(() => import('@/features/admin/views/AdminRoutingView').then(m => ({ default: m.AdminRoutingView })))
const AdminDealersView = lazy(() => import('@/features/admin/views/AdminDealersView').then(m => ({ default: m.AdminDealersView })))

// Lazy load customer orders
const CustomerOrdersView = lazy(() => import('@/features/customer-orders').then(m => ({ default: m.CustomerOrdersView })))

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <p className="text-muted-foreground">Laster...</p>
    </div>
  )
}

function ProtectedRoute() {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingFallback />
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <Outlet />
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return <LoadingFallback />
  }

  if (user) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Public routes - no auth required */}
          <Route path="/chat/:accessToken" element={<CustomerChatView />} />
          <Route path="/verify/:accessToken" element={<VerifyDeliveryView />} />
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/sok" element={<FeatureGuard feature="pckPocket"><PckPocketV3 /></FeatureGuard>} />
              <Route path="/ordrelogg" element={<OrdreloggView />} />
              <Route path="/ordrelogg/:threadId" element={<OrdreloggView />} />
              <Route path="/verksted" element={<FeatureGuard feature="verksted"><VerkstedDashboard /></FeatureGuard>} />
              <Route path="/verksted/inbox" element={<FeatureGuard feature="verksted"><VerkstedInbox /></FeatureGuard>} />
              <Route path="/verksted/in-progress" element={<FeatureGuard feature="verksted"><VerkstedInProgress /></FeatureGuard>} />
              <Route path="/verksted/completed" element={<FeatureGuard feature="verksted"><VerkstedCompleted /></FeatureGuard>} />
              <Route path="/verksted/history" element={<FeatureGuard feature="verksted"><VerkstedHistory /></FeatureGuard>} />
              <Route path="/admin" element={<FeatureGuard feature="admin"><AdminDashboard /></FeatureGuard>} />
              <Route path="/admin/routing" element={<FeatureGuard feature="admin"><AdminRoutingView /></FeatureGuard>} />
              <Route path="/admin/dealers" element={<FeatureGuard feature="admin"><AdminDealersView /></FeatureGuard>} />
            </Route>
            {/* Verksted case detail - fullscreen */}
            <Route path="/verksted/case/:id" element={<FeatureGuard feature="verksted"><VerkstedCaseView /></FeatureGuard>} />
            {/* Order detail - fullscreen */}
            <Route path="/ordre/:orderId" element={<FeatureGuard feature="pckPocket"><OrderDetailView /></FeatureGuard>} />
            {/* Customer orders - fullscreen */}
            <Route path="/customer/:customerId" element={<CustomerOrdersView />} />
            {/* Delivery routes - fullscreen layout */}
            <Route path="/import" element={<ImportView />} />
            <Route path="/delivery/:orderId/import" element={<ImportView />} />
            <Route path="/delivery/:orderId/park" element={<FeatureGuard feature="parking"><ParkView /></FeatureGuard>} />
            <Route path="/delivery/:orderId/parked" element={<FeatureGuard feature="parking"><ParkedView /></FeatureGuard>} />
            <Route path="/delivery/:orderId/split" element={<FeatureGuard feature="splitOrder"><SplitView /></FeatureGuard>} />
            <Route path="/delivery/:orderId/partial" element={<FeatureGuard feature="splitOrder"><PartialOrderView /></FeatureGuard>} />
            <Route path="/delivery/:orderId/pending" element={<PendingView />} />
            <Route path="/delivery/:orderId/delivered" element={<DeliveredView />} />
            {/* Legacy redirects */}
            <Route path="/search" element={<Navigate to="/" replace />} />
            <Route path="/delivery/new" element={<Navigate to="/" replace />} />
            <Route path="/delivery/:orderId" element={<Navigate to="/" replace />} />
          </Route>
          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default App
